import { useState } from 'react';
import { DayPicker } from 'react-day-picker';
import { useTranslation } from 'react-i18next';

import { parseDateOnly } from '@/shared/lib/dates';

import { cn } from '../cn';
import { CalendarIcon, ChevronEndIcon, ChevronStartIcon } from '../icons';
import { fieldClassName } from './input';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

export interface DatePickerProps {
  id?: string;
  /** Calendar date as YYYY-MM-DD, or null. */
  value: string | null;
  onChange: (value: string | null) => void;
  /** Inclusive bounds, YYYY-MM-DD. */
  min?: string;
  max?: string;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
}

function toDateOnly(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function fromDateOnly(v: string): Date {
  const u = parseDateOnly(v);
  return new Date(u.getUTCFullYear(), u.getUTCMonth(), u.getUTCDate());
}

/** "14/03/1991", "14-3-1991", "1991-03-14" -> "1991-03-14"; anything else -> null. */
export function parseTypedDate(text: string): string | null {
  const t = text.trim();
  let y: number, m: number, d: number;
  const dmy = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/.exec(t);
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(t);
  if (dmy) [d, m, y] = [Number(dmy[1]), Number(dmy[2]), Number(dmy[3])];
  else if (iso) [y, m, d] = [Number(iso[1]), Number(iso[2]), Number(iso[3])];
  else return null;
  const date = new Date(Date.UTC(y, m - 1, d));
  if (date.getUTCFullYear() !== y || date.getUTCMonth() !== m - 1 || date.getUTCDate() !== d) return null;
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}
const toTyped = (v: string | null) => (v && /^\d{4}-\d{2}-\d{2}$/.test(v) ? `${v.slice(8, 10)}/${v.slice(5, 7)}/${v.slice(0, 4)}` : (v ?? ''));

/**
 * Date-only field: type DD/MM/YYYY (fast keyboard entry) or pick from the calendar, which has
 * month and year dropdowns for dates far from today (dates of birth, expiry dates).
 * Values are calendar dates (YYYY-MM-DD) with no time-zone shifts. Text that is not a valid date
 * is passed through unchanged so the form's schema reports it.
 */
export function DatePicker({ id, value, onChange, min, max, disabled, placeholder, className, ...aria }: DatePickerProps) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(() => toTyped(value));
  const [lastValue, setLastValue] = useState(value);
  if (lastValue !== value) {
    setLastValue(value);
    if (parseTypedDate(text) !== value) setText(toTyped(value));
  }
  const selected = value && parseTypedDate(value) ? fromDateOnly(value) : undefined;
  const dir = document.documentElement.dir === 'rtl' ? 'rtl' : 'ltr';
  const today = new Date();
  const startMonth = min ? fromDateOnly(min) : new Date(today.getFullYear() - 100, 0, 1);
  const endMonth = max ? fromDateOnly(max) : new Date(today.getFullYear() + 30, 11, 31);
  const commit = (raw: string) => {
    if (!raw.trim()) return onChange(null);
    onChange(parseTypedDate(raw) ?? raw);
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className={cn(fieldClassName, 'flex h-8 items-center gap-1 pe-0.5 focus-within:border-accent', aria['aria-invalid'] && 'border-negative', className)}>
        <input
          id={id}
          type="text"
          inputMode="numeric"
          dir="ltr"
          autoComplete="off"
          disabled={disabled}
          placeholder={placeholder ?? t('form.datePlaceholder')}
          aria-invalid={aria['aria-invalid']}
          aria-describedby={aria['aria-describedby']}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onBlur={() => commit(text)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit(text);
          }}
          className="tabular h-full min-w-0 flex-1 bg-transparent ps-2.5 text-body text-ink outline-none placeholder:text-ink-muted"
        />
        <PopoverTrigger asChild>
          <button type="button" disabled={disabled} aria-label={t('form.pickDate')} className="inline-flex size-7 shrink-0 items-center justify-center rounded-sm text-ink-muted hover:bg-surface-muted hover:text-ink">
            <CalendarIcon size={14} />
          </button>
        </PopoverTrigger>
      </div>
      <PopoverContent className="p-3">
        <DayPicker
          mode="single"
          dir={dir}
          lang={i18n.language}
          selected={selected}
          defaultMonth={selected ?? (max && fromDateOnly(max) < today ? fromDateOnly(max) : undefined)}
          captionLayout="dropdown"
          startMonth={startMonth}
          endMonth={endMonth}
          weekStartsOn={1}
          disabled={[...(min ? [{ before: fromDateOnly(min) }] : []), ...(max ? [{ after: fromDateOnly(max) }] : [])]}
          onSelect={(d) => {
            const next = d ? toDateOnly(d) : null;
            setText(toTyped(next));
            onChange(next);
            setOpen(false);
          }}
          components={{
            Chevron: ({ orientation }) => (orientation === 'left' ? <ChevronStartIcon size={14} /> : <ChevronEndIcon size={14} />),
          }}
          classNames={{
            root: 'text-body',
            months: 'flex flex-col',
            month: 'flex flex-col gap-2',
            month_caption: 'flex h-7 items-center ps-1 font-medium text-ink',
            dropdowns: 'flex items-center gap-1',
            dropdown_root: 'relative inline-flex items-center rounded-sm border border-border px-1.5 h-7',
            dropdown: 'absolute inset-0 opacity-0 cursor-pointer',
            caption_label: 'inline-flex items-center gap-1 text-label',
            nav: 'absolute end-3 top-3 flex items-center gap-1',
            button_previous: 'inline-flex size-7 items-center justify-center rounded-sm text-ink-secondary hover:bg-surface-muted disabled:opacity-40',
            button_next: 'inline-flex size-7 items-center justify-center rounded-sm text-ink-secondary hover:bg-surface-muted disabled:opacity-40',
            weekdays: 'flex',
            weekday: 'w-8 text-center text-meta font-normal text-ink-muted',
            week: 'flex',
            day: 'size-8 p-0 text-center',
            day_button: 'tabular size-8 rounded-sm text-table hover:bg-surface-muted aria-selected:bg-primary aria-selected:text-primary-foreground',
            selected: 'text-primary-foreground',
            today: 'font-semibold text-accent',
            outside: 'text-ink-muted/60',
            disabled: 'pointer-events-none text-ink-muted/40',
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
